export interface TemplateItem {
  id: string;
  text: string;
  sort_order: number;
  created_at: string;
}

export type ProjectType = 'website' | 'video' | 'pakkelosning' | 'klippekort';

export interface ChecklistTemplate {
  id: string;
  project_type: ProjectType;
  phase: string;
  name: string;
  items: TemplateItem[];
  active_instance_count: number;
  created_at: string;
  updated_at: string;
}

async function asJson<T>(r: Response): Promise<T> {
  if (!r.ok) {
    const data = (await r.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? `HTTP ${r.status}`);
  }
  return (await r.json()) as T;
}

export async function listTemplates(): Promise<ChecklistTemplate[]> {
  const r = await fetch('/api/admin/checklist-templates');
  const json = await asJson<{ templates: ChecklistTemplate[] }>(r);
  return json.templates;
}

export interface CreateTemplateBody {
  project_type: ProjectType;
  phase: string;
  name: string;
  items?: Array<{ text: string; sort_order?: number }>;
}

export async function createTemplate(body: CreateTemplateBody): Promise<ChecklistTemplate> {
  const r = await fetch('/api/admin/checklist-templates', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await asJson<{ template: ChecklistTemplate }>(r);
  return json.template;
}

export async function updateTemplateName(id: string, name: string): Promise<ChecklistTemplate> {
  const r = await fetch(`/api/admin/checklist-templates/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  const json = await asJson<{ template: ChecklistTemplate }>(r);
  return json.template;
}

export async function addTemplateItem(
  id: string,
  body: { text: string; sort_order?: number },
): Promise<{ template: ChecklistTemplate; propagated_count: number }> {
  const r = await fetch(`/api/admin/checklist-templates/${id}/items`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return asJson<{ template: ChecklistTemplate; propagated_count: number }>(r);
}

export async function updateTemplateItem(
  templateId: string,
  itemId: string,
  text: string,
): Promise<ChecklistTemplate> {
  const r = await fetch(`/api/admin/checklist-templates/${templateId}/items/${itemId}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  const json = await asJson<{ template: ChecklistTemplate }>(r);
  return json.template;
}

export async function deleteTemplateItem(
  templateId: string,
  itemId: string,
): Promise<ChecklistTemplate> {
  const r = await fetch(`/api/admin/checklist-templates/${templateId}/items/${itemId}`, {
    method: 'DELETE',
  });
  const json = await asJson<{ template: ChecklistTemplate }>(r);
  return json.template;
}
